// 選択中ファイルの一括ゴミ箱移動
import { ref, computed, watch, onMounted } from 'vue';

export const useBulkTrash = (basketCount, clearBasket, basketItemsList, basketVideoIds, maxInputVars, bulkMaxItems) => {
  const bulkDeleteMode = ref('selected');
  const bulkVersionGroups = ref(new Map());
  const bulkVersionsLoading = ref(false);
  const bulkVersionsError = ref(false);

  const bulkVersionedItems = computed(() =>
    (basketItemsList.value || []).filter((item) => (item.versionCount || 1) > 1)
  );

  const bulkHasVersionedItems = computed(() => bulkVersionedItems.value.length > 0);

  const bulkExtraVersionEntries = computed(() => {
    if (bulkDeleteMode.value !== 'all') {
      return [];
    }
    const selectedIds = new Set((basketVideoIds.value || []).map((id) => String(id)));
    const seen = new Set();
    const result = [];
    bulkVersionGroups.value.forEach((versions) => {
      (versions || []).forEach((version) => {
        const id = String(version.id);
        if (selectedIds.has(id) || seen.has(id) || version.deleted_at) {
          return;
        }
        seen.add(id);
        result.push(version);
      });
    });
    return result.sort((a, b) => Number(a.id) - Number(b.id));
  });

  const bulkExtraVersionIds = computed(() =>
    bulkExtraVersionEntries.value.map((v) => Number(v.id))
  );

  const bulkTrashTotalCount = computed(() =>
    (basketVideoIds.value || []).length + (bulkDeleteMode.value === 'all' ? bulkExtraVersionIds.value.length : 0)
  );

  // ゴミ箱移動のフォームは videoids[] のほかに _token と view も送るためその 2 件を差し引き、
  // サーバ側が「入力変数の総数が max_input_vars 以上」で弾く境界に掛からないようさらに 1 件余裕を持たせる。
  // これを超えると PHP が超過分を捨ててしまい、一部だけ移動して取りこぼしに気づけなくなる。
  // サーバー側の件数上限 (config exa.bulk_max_items) とのうち小さい方を使う
  const bulkTrashMaxCount = computed(() => {
    const limit = Number(maxInputVars?.value || 0);
    const fromInputVars = limit > 0 ? limit - 3 : 0;
    const cap = Number(bulkMaxItems?.value || 0);
    const candidates = [fromInputVars, cap].filter((n) => n > 0);
    return candidates.length ? Math.min(...candidates) : 0;
  });

  const bulkTrashLimitExceeded = computed(() =>
    bulkTrashMaxCount.value > 0 && bulkTrashTotalCount.value > bulkTrashMaxCount.value
  );

  // バスケットから×ボタン等で除外されたアイテムのバージョン一覧を bulkVersionGroups からも追従削除する。
  // 放置すると getFullVersionHistory() が対象自身を含むバージョン系列を返す都合上、
  // 除外したアイテム自身が bulkExtraVersionEntries（未選択の残りバージョン）に再出現してしまう。
  watch(basketItemsList, (list) => {
    const validIds = new Set((list || []).map((item) => String(item.id)));
    const next = new Map();
    let dirty = false;
    bulkVersionGroups.value.forEach((versions, key) => {
      if (validIds.has(key)) {
        next.set(key, versions);
      } else {
        dirty = true;
      }
    });
    if (dirty) {
      bulkVersionGroups.value = next;
    }
  });

  const fetchBulkVersionGroups = async () => {
    const targets = bulkVersionedItems.value;
    if (!targets.length) {
      bulkVersionGroups.value = new Map();
      bulkVersionsError.value = false;
      return;
    }
    bulkVersionsLoading.value = true;
    bulkVersionsError.value = false;
    try {
      const results = await Promise.allSettled(
        targets.map((item) => window.axios.get(`/api/videos/${item.id}/versions`))
      );
      const next = new Map();
      let hasError = false;
      results.forEach((result, index) => {
        const item = targets[index];
        if (result.status === 'fulfilled') {
          const payload = result.value?.data?.versions ?? [];
          next.set(String(item.id), Array.isArray(payload) ? payload : []);
        } else {
          hasError = true;
        }
      });
      bulkVersionGroups.value = next;
      bulkVersionsError.value = hasError;
    } catch (error) {
      bulkVersionsError.value = true;
    } finally {
      bulkVersionsLoading.value = false;
    }
  };

  const showBulkTrashModal = () => {
    if (basketCount.value < 1) return;
    bulkDeleteMode.value = 'selected';
    bulkVersionGroups.value = new Map();
    bulkVersionsError.value = false;
    if (window.jQuery) {
      window.jQuery('#bulkTrashModal').modal('show');
    }
    if (bulkHasVersionedItems.value) {
      fetchBulkVersionGroups();
    }
  };

  const submitBulkTrash = () => {
    if (bulkTrashLimitExceeded.value) {
      return;
    }
    if (window.jQuery) {
      window.jQuery('#bulkTrashModal').modal('hide');
    }
    const form = document.querySelector('[action*="bulkremove"]');
    if (!form) return;
    form.submit();
  };

  // サーバー側でエラーになったときは棚を空にしてはいけないため、成功時のみ
  // data-bulk-trash-succeeded を見て棚をクリアする
  onMounted(() => {
    const dataSource = document.getElementById('dashboard-data-source');
    if (dataSource?.getAttribute('data-bulk-trash-succeeded') === '1') {
      clearBasket();
    }
  });

  return {
    bulkDeleteMode,
    bulkHasVersionedItems,
    bulkVersionedItems,
    bulkVersionsLoading,
    bulkVersionsError,
    bulkExtraVersionEntries,
    bulkExtraVersionIds,
    bulkTrashTotalCount,
    bulkTrashMaxCount,
    bulkTrashLimitExceeded,
    fetchBulkVersionGroups,
    showBulkTrashModal,
    submitBulkTrash,
  };
};
